import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { GithubConnectionCard } from '../components/GithubConnectionCard';
import { GithubInstallationChooser } from '../components/GithubInstallationChooser';
import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import { useGithubInstallResultToast } from '../components/useGithubInstallResultToast';

export function ScmSection(): React.ReactElement {
  useGithubInstallResultToast();
  // `?github_choose=<token>` is where the GitHub sign-in callback lands when this admin can reach
  // installations the workspace has not linked. Hold the token in state and strip it from the URL, so a
  // refresh does not reopen a chooser whose token may already be spent.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlChoiceToken = searchParams.get('github_choose');
  const [choiceToken, setChoiceToken] = useState<string | null>(urlChoiceToken);
  const [seenChoiceToken, setSeenChoiceToken] = useState<string | null>(urlChoiceToken);
  if (urlChoiceToken && urlChoiceToken !== seenChoiceToken) {
    setSeenChoiceToken(urlChoiceToken);
    setChoiceToken(urlChoiceToken);
  }
  useEffect(() => {
    if (!urlChoiceToken) return;
    const next = new URLSearchParams(searchParams);
    next.delete('github_choose');
    setSearchParams(next, { replace: true });
  }, [urlChoiceToken, searchParams, setSearchParams]);

  // The chooser sits beside the connection card, not inside it, so it stays mounted while the card
  // moves between its loading, error and loaded states.
  return (
    <>
      <ScmConnection />
      {choiceToken && (
        <GithubInstallationChooser
          choiceToken={choiceToken}
          open
          onOpenChange={(open) => !open && setChoiceToken(null)}
        />
      )}
    </>
  );
}

function ScmConnection(): React.ReactElement {
  const orpc = useOrpc();

  // Show the skeleton until the GitHub connection loads, also when the browser is offline. Nothing failed
  // then, so the "Failed to load" callout would be wrong. (`isLoading` is false for a query paused
  // offline, and `isPending` is true, so the check uses `isPending`.)
  const { data, isPending, error } = useQuery(orpc.settings.scm.listRepos.queryOptions());

  const header = <SettingsSectionHeader title="GitHub" description="Connect GitHub so the agent can read your code." />;

  if (isPending) {
    return (
      <section>
        {header}
        <Skeleton className="h-18 w-full rounded-lg" />
      </section>
    );
  }

  if (!data) {
    return (
      <section>
        {header}
        <Callout variant="error">{error?.message ?? 'Failed to load the GitHub connection.'}</Callout>
      </section>
    );
  }

  return (
    <section>
      {header}
      <GithubConnectionCard data={data} />
    </section>
  );
}
